import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import "./landing.css";

/**
 * Sanftes Fade-in beim ins-Viewport-scrollen. Nutzt native IntersectionObserver
 * (keine neue Dependency). Respektiert prefers-reduced-motion.
 */
export function useRevealOnScroll() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { el.classList.add("ag-revealed"); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { el.classList.add("ag-revealed"); io.unobserve(el); } });
    }, { threshold: 0.12, rootMargin: "0px 0px -60px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}
import Logo from "@/components/Logo";
import landing from "@/content/landing";
import HeroSection from "./HeroSection";
import BrandMessage from "./BrandMessage";
import StoryGrid from "./StoryGrid";
import LifeTreePreview from "./LifeTreePreview";
import TrustRingShowcase from "./TrustRingShowcase";
import TrustCardShowcase from "./TrustCardShowcase";
import PeopleSection from "./PeopleSection";
import LinaTeaser from "./LinaTeaser";
import PlatformShowcase from "./PlatformShowcase";
import WaitlistPreview from "./WaitlistPreview";
import PublicFooter from "./PublicFooter";

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const nav = landing.navigation;

  useEffect(() => {
    if (menuOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  // Close menu on ESC (no new dependency)
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="min-h-screen bg-ag-paper text-ag-ink ag-paper-grain" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-slate-200/60 bg-ag-paper/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 lg:px-8">
          <a href="#top" className="flex items-center" aria-label="ALLES GEREGELT · Startseite" data-testid="landing-header-logo">
            <Logo variant="full" tone="light" className="h-10 w-auto md:h-12" />
          </a>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-8 md:flex" aria-label="Hauptnavigation">
            {nav.items.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="text-sm text-slate-600 transition hover:text-ag-ink"
                data-testid={`landing-nav-${item.id}`}
              >
                {item.label}
              </a>
            ))}
            <Link
              to={nav.loginHref}
              className="inline-flex min-h-11 items-center rounded-full border border-ag-ink/80 px-5 text-sm font-medium text-ag-ink transition hover:bg-ag-ink hover:text-white"
              data-testid="landing-nav-login"
            >
              {nav.loginLabel}
            </Link>
            <a
              href="#waitlist"
              className="hidden lg:inline-flex min-h-11 items-center gap-2 rounded-full bg-ag-ink px-5 text-sm font-medium text-white shadow-sm transition hover:bg-ag-ink/90"
              data-testid="landing-nav-cta"
            >
              Von Anfang an dabei sein
            </a>
          </nav>

          {/* Mobile burger */}
          <button
            type="button"
            className="flex h-11 w-11 items-center justify-center rounded-full text-ag-ink md:hidden"
            aria-label={menuOpen ? nav.menuClose : nav.menuOpen}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="landing-menu-toggle"
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu panel */}
        {menuOpen && (
          <div className="border-t border-slate-200/60 bg-ag-paper md:hidden" data-testid="landing-menu-panel">
            <nav className="mx-auto flex max-w-6xl flex-col gap-1 px-5 py-4" aria-label="Mobile Navigation">
              {nav.items.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={closeMenu}
                  className="rounded-lg px-3 py-3 text-base text-slate-700 hover:bg-slate-100"
                  data-testid={`landing-nav-mobile-${item.id}`}
                >
                  {item.label}
                </a>
              ))}
              <Link
                to={nav.loginHref}
                onClick={closeMenu}
                className="mt-2 inline-flex min-h-12 items-center justify-center rounded-full border border-ag-ink/80 px-5 text-center text-sm font-medium text-ag-ink"
                data-testid="landing-nav-mobile-login"
              >
                {nav.loginLabel}
              </Link>
              <a
                href="#waitlist"
                onClick={closeMenu}
                className="inline-flex min-h-12 items-center justify-center rounded-full bg-ag-ink px-5 text-center text-sm font-medium text-white"
                data-testid="landing-nav-mobile-cta"
              >
                Von Anfang an dabei sein
              </a>
            </nav>
          </div>
        )}
      </header>

      <main id="top">
        <HeroSection />
        <div className="ag-hairline" aria-hidden="true" />
        <BrandMessage />
        <div className="ag-hairline" aria-hidden="true" />
        <StoryGrid />
        <LifeTreePreview />
        <div className="ag-hairline" aria-hidden="true" />
        <TrustRingShowcase />
        <TrustCardShowcase />
        <div className="ag-hairline" aria-hidden="true" />
        <PeopleSection />
        <LinaTeaser />
        <div className="ag-hairline" aria-hidden="true" />
        <PlatformShowcase />
        <WaitlistPreview />
      </main>

      <PublicFooter />
    </div>
  );
}
